import React, { useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@/components/button";
import { IconButton } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useMediaQuery } from "@mui/material";
import { Close } from "@mui/icons-material";
import PreLoader from "@/components/preLoader";
import ProductCard from "./ProductCard";

import { selectLocalization } from "@/store/store/selectors";

import { useMutation } from "@apollo/client";
import { UPDATE_PRODUCTS } from "@/services/productService";

import { Colors } from "@/theme";
import useStyles from "./styles";
import { getStoreAddressString } from "@/utils/otherUtil";
import { STORE_INTEGRATION_TYPE } from "@/constants/config";
import { MERCHANT_ID } from "@/constants/env";
import moment from "moment";

const AddDeliveryModal = ({
  open,
  onClose,
  store,
  products,
  stockTypes,
  onUpdateSuccess,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const colNum = isDesktop ? 4 : 1;

  const localization = useSelector(selectLocalization);

  const currentTime = useRef(dayjs(new Date()));

  const [productsForPurchase, setProductsForPurchase] = useState(
    products.map((product) => ({
      id: product.id,
      product_code: product.product_code,
      purchase_price: "0",
      stock_types: stockTypes.map((type) => ({ ...type, amount: "0" })),
      time: currentTime.current,
    }))
  );

  const [updateProductsMutation, { loading }] = useMutation(UPDATE_PRODUCTS, {
    context: {
      headers: {
        merchantId: MERCHANT_ID,
        storeId: store.id,
      },
    },
    onCompleted: (data) => {
      onUpdateSuccess();
      onClose();
    },
  });

  const onChangeProduct = (product, key, value) => {
    setProductsForPurchase(
      productsForPurchase.map((item) =>
        item.id === product.id ? { ...item, [key]: value } : item
      )
    );
  };

  const onAddStock = () => {
    updateProductsMutation({
      variables: {
        products: productsForPurchase
          .filter(
            (item) =>
              !!item.stock_types?.find(
                (stock_type) => parseFloat(stock_type.amount) > 0
              )
          )
          ?.map((product) => ({
            id: product.id,
            stocks: product.stock_types?.map((stock_type) => ({
              ...(parseFloat(product.purchase_price) > 0 && {
                purchase_price: product.purchase_price,
              }),
              stock: stock_type.amount,
              stock_type_id: stock_type.id,
              update_time: product.time.toISOString().replace("Z", ""),
            })),
          })),
      },
    });
  };

  return (
    <>
      <Dialog
        className={classes.container}
        fullWidth
        open={open}
        onClose={onClose}
      >
        <DialogTitle className={classes.title}>
          {t("purchase_screen.add_delivery")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
          }}
        >
          <Close fontSize="large" sx={{ color: Colors.GRAY_DARK }} />
        </IconButton>
        <DialogContent className={classes.content}>
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            <img src={store.brand?.logo?.thumbnail} width={80} height={80} />
            <Box ml={4}>
              <Box textAlign={"center"} mb={3}>
                <span className={classes.storeNameText}>
                  {`${store.name} (${
                    store.integrations?.find(
                      (item) =>
                        item.integration_type ===
                        STORE_INTEGRATION_TYPE.LIVE_PRICE
                    )?.integration_id
                  })`}
                </span>
              </Box>
              <Box textAlign={"center"} maxWidth={220} margin={"auto"}>
                <span className={classes.addressText}>
                  {getStoreAddressString(store.address)}
                </span>
              </Box>
            </Box>
          </Box>

          <Grid container component={"main"} mt={10}>
            {productsForPurchase?.map((product, id) => (
              <Grid key={id} item xs={12 / colNum}>
                <ProductCard
                  product={product}
                  isLast={(id + 1) % colNum === 0}
                  localization={localization}
                  onChange={(key, value) =>
                    onChangeProduct(product, key, value)
                  }
                />
              </Grid>
            ))}
          </Grid>
          <Box
            padding={10}
            paddingTop={0}
            display={"flex"}
            justifyContent={"flex-end"}
          >
            <Button
              disabled={false}
              color="primary"
              variant="contained"
              sx={{ mt: 4 }}
              className={classes.updateButton}
              onClick={onAddStock}
            >
              {t("purchase_screen.add_stock")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {loading && <PreLoader size={40} />}
    </>
  );
};

export default AddDeliveryModal;
